import { useNeedsSideways } from "@/lib/useNeedsSideways";
import type { FxRate } from "@/types";
import { formatDate } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

export function statusOf(rate: FxRate): "Active" | "Superseded" {
  return rate.active ? "Active" : "Superseded";
}

type ColDef = { header: string; width: number; align: "left" | "right" };
const COLS: ColDef[] = [
  { header: "Currency",       width: 100, align: "left"  },
  { header: "Rate",           width: 110, align: "right" },
  { header: "Effective From", width: 140, align: "left"  },
  { header: "Effective To",   width: 140, align: "left"  },
  { header: "Source",         width: 170, align: "left"  },
  { header: "Status",         width: 110, align: "left"  },
];

// A value the backend does not hold, or holds under a key this build does
// not recognise. Never guessed: "Customs Notified" is a claim about where a
// published figure came from.
function Dash() {
  return <span style={{ color: semanticColors.textPlaceholder }}>—</span>;
}

function SourcePill({ source, muted }: { source: NonNullable<FxRate["source"]>; muted: boolean }) {
  const notified = source === "Customs Notified";
  const fg = muted ? semanticColors.textPlaceholder : (notified ? semanticColors.accentTextOnTint : colors.amber600);
  const bg = muted ? semanticColors.hoverBg : (notified ? colors.indigo50 : colors.amber50);
  const bd = muted ? semanticColors.border : (notified ? colors.indigo100 : colors.amber200);
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center",
        height: heights.pill, padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
        color: fg, backgroundColor: bg, border: `1px solid ${bd}`,
      }}
    >
      {source}
    </span>
  );
}

function StatusPill({ status }: { status: "Active" | "Superseded" }) {
  const fg = status === "Active" ? colors.green600 : semanticColors.textMuted;
  const bg = status === "Active" ? colors.green50 : semanticColors.hoverBg;
  const bd = status === "Active" ? colors.green100 : semanticColors.border;
  return (
    <span
      data-testid={`fxrate-status-${status.toLowerCase()}`}
      style={{
        display: "inline-flex", alignItems: "center",
        height: heights.pill, padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
        color: fg, backgroundColor: bg, border: `1px solid ${bd}`,
      }}
    >
      {status}
    </span>
  );
}

const ACTION_WIDTH = 96;

export function FxRatesTable({
  rows,
  selectedId,
  onRowClick,
  onCorrect,
}: {
  rows: FxRate[];
  selectedId?: string;
  onRowClick: (r: FxRate) => void;
  /** Admin only. Offered on a row only when the SERVER says it is correctable. */
  onCorrect?: (r: FxRate) => void;
}) {
  const minWidth = COLS.reduce((s, c) => s + c.width, 0) + (onCorrect ? ACTION_WIDTH : 0);
  const [ref, sideways] = useNeedsSideways(minWidth);
  return (
    <div
      ref={ref}
      data-testid="fx-rates-table"
      data-sideways={sideways ? "true" : "false"}
      style={{
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        backgroundColor: semanticColors.surface,
        // Sideways only; never a vertical scroller of its own.
        overflowX: sideways ? "auto" : "visible", overflowY: "visible",
      }}
    >
      <div style={{ minWidth }}>
        {/* Header -- sticky against the page scroller. */}
        <div data-testid="fx-rates-header" style={{
          display: "flex", height: heights.control,
          position: sideways ? "static" : "sticky", top: 0, zIndex: 1,
          backgroundColor: semanticColors.panelBg, borderBottom: `1px solid ${semanticColors.border}`,
        }}>
          {COLS.map((c) => (
            <div key={c.header} style={{
              width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
              display: "flex", alignItems: "center",
              justifyContent: c.align === "right" ? "flex-end" : "flex-start",
              fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: semanticColors.textSecondary,
              textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
              whiteSpace: "nowrap",
            }}>
              {c.header}
            </div>
          ))}
          {onCorrect && <div style={{ width: ACTION_WIDTH, minWidth: ACTION_WIDTH }} />}
        </div>

        {/* Body */}
        <div>
          {rows.map((r) => {
            const status = statusOf(r);
            const superseded = status === "Superseded";
            const textColor = superseded ? semanticColors.textPlaceholder : semanticColors.textPrimary;
            const isSelected = r.id === selectedId;
            return (
              <div
                key={r.id}
                data-testid={`fxrate-row-${r.id}`}
                data-status={status}
                className="material-row"
                onClick={() => onRowClick(r)}
                style={{
                  display: "flex", height: heights.row,
                  borderBottom: `1px solid ${semanticColors.border}`,
                  backgroundColor: isSelected ? semanticColors.hoverBg : semanticColors.surface,
                  cursor: "pointer",
                }}
              >
                <div style={{ width: COLS[0].width, minWidth: COLS[0].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                              color: textColor, fontWeight: typography.fontWeight.medium }}>
                  {r.currency || <Dash />}
                </div>
                <div style={{ width: COLS[1].width, minWidth: COLS[1].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", justifyContent: "flex-end",
                              fontSize: typography.fontSize.sm, color: textColor, fontVariantNumeric: "tabular-nums" }}>
                  {r.rate.toFixed(2)}
                </div>
                <div style={{ width: COLS[2].width, minWidth: COLS[2].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                              color: textColor, fontVariantNumeric: "tabular-nums" }}>
                  {r.effectiveFrom ? formatDate(r.effectiveFrom) : <Dash />}
                </div>
                <div style={{ width: COLS[3].width, minWidth: COLS[3].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                              color: r.effectiveTo ? textColor : semanticColors.textPlaceholder,
                              fontVariantNumeric: "tabular-nums" }}>
                  {r.effectiveTo ? formatDate(r.effectiveTo) : "—"}
                </div>
                <div style={{ width: COLS[4].width, minWidth: COLS[4].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center" }}>
                  {r.source ? <SourcePill source={r.source} muted={superseded} /> : <Dash />}
                </div>
                <div style={{ width: COLS[5].width, minWidth: COLS[5].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center" }}>
                  <StatusPill status={status} />
                </div>
                {onCorrect && (
                  <div style={{ width: ACTION_WIDTH, minWidth: ACTION_WIDTH, padding: `0 ${spacing[8]}px`,
                                display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                    {r.correctable && (
                      <button
                        data-testid={`fx-correct-open-${r.id}`}
                        onClick={(e) => { e.stopPropagation(); onCorrect(r); }}
                        style={{
                          height: heights.pill + spacing[4], padding: `0 ${spacing[8]}px`,
                          border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                          backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                          fontSize: typography.fontSize.xs, cursor: "pointer",
                        }}
                      >
                        Correct
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {rows.length === 0 && (
            <div
              data-testid="fx-rates-empty"
              style={{
                padding: spacing[40], textAlign: "center",
                fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder,
              }}
            >
              No exchange rates match the current filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
